/**
 * VOYAGE RADAR'S vessel popup markup.
 *
 * Pure string builders — no Leaflet, no Angular, no component state — so any map
 * component that draws Voyage Radar vessels can render the same card.
 *
 * PROVIDER-SIDE since D11, having spent the refactor in `shared/`. The file
 * said so itself from the first line, and the evidence agreed: all three
 * importers are Voyage Radar's, and the two in the host component sit inside
 * `type === 'VOYAGERADAR'` guards. What it BUILDS is this provider's priced
 * actions — the by-MMSI update, port calls, nearby — which is the same test
 * that moved `vessel-search.model.ts` (D9).
 *
 * Its tolerance for both field spellings (`Name`/`name`, `MMSI`/`mmsi`) is NOT
 * evidence of a second consumer, and that argument has now been made and
 * rejected twice. AISstream draws its own card and never imported this one.
 *
 * Nothing in `shared/` breaks by this move, because nothing in `shared/` ever
 * imported it: `map-core/popup.host.ts` takes `buildVesselCard` and
 * `buildClusterCard` as INJECTED options. That seam is what made the file
 * movable — had the host imported it directly, it would have had to stay.
 *
 * The classes (`ais-vessel-popup`, `ais-popup__*`) are styled globally in
 * `styles.scss`, not in a component stylesheet: Leaflet mounts popups outside
 * the component's DOM, so scoped styles would never reach them.
 */

import { VoyagePopupActions } from './vessel-popup.types';
// Shared: escaping is a fact about HTML, not about this card (D11).
import { esc } from '../shared/models/html-escape';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mon, 27 Jul 2026 17:24:08 UTC", or "—" when the stamp is missing/unparseable. */
function formatPopupTime(raw: any): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${DAYS[d.getUTCDay()]}, ${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

/** One key/value line, or nothing at all when there is no value to show. */
function row(label: string, val: any, suffix = ''): string {
  if (val == null || val === '' || val === 'N/A') return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">${label}:</span>` +
    `<span class="ais-popup__v">${esc(val)}${suffix}</span></div>`
  );
}

/**
 * Decimal places on a printed vessel position.
 *
 * The WeatherEx point tooltip prints 2, and this row copies its grammar
 * ("Lat: …, Lng: …") so the two read as one product — but not its precision. A
 * WeatherEx value describes a forecast CELL kilometres across, where the third
 * decimal would be invented; an AIS fix is a single hull, and 2 dp rounds it
 * into a ~1.1 km box, which is a different berth, sometimes a different
 * anchorage. 4 dp (~11 m) is the position the operator can actually act on.
 */
const POSITION_DP = 4;

/**
 * A coordinate only if it is a real, in-range number — never a printed NaN.
 *
 * The empty-string guard is load-bearing, not defensive noise:
 * `toVoyageVesselFeature` writes `Lat: ''` for a hull with no position, and
 * `Number('')` is 0 — so without it a vessel whose fix is missing would be
 * printed as sitting at 0.0000, 0.0000 in the Gulf of Guinea.
 */
function coord(value: any, limit: number): number | null {
  if (value == null || (typeof value === 'string' && !value.trim())) return null;
  const n = Number(value);
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
}

/**
 * "Lat: 1.2646, Lng: 103.8201" — the same label grammar the WeatherEx point
 * tooltip uses, so a coordinate reads the same wherever the map shows one.
 *
 * Renders nothing at all when either half is missing or out of range: a hull
 * whose fix did not survive the feed is better shown without a position than
 * with a plausible-looking one at the equator.
 */
function positionRow(p: any): string {
  const lat = coord(p['Lat'] ?? p['lat'] ?? p['latitude'], 90);
  const lng = coord(p['Lng'] ?? p['lng'] ?? p['longitude'], 180);
  if (lat == null || lng == null) return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">Position:</span>` +
    `<span class="ais-popup__v ais-popup__pos">` +
    `Lat: ${lat.toFixed(POSITION_DP)}, Lng: ${lng.toFixed(POSITION_DP)}</span></div>`
  );
}

/**
 * The popup's inner rows, without the card wrapper.
 *
 * Optional fields (Heading, IMO, Call Sign, Flag, Country, Length, Beam, Rate of
 * Turn) render only when present, so a history track-point — which carries far
 * fewer fields than a live bbox feature — still produces a compact card.
 *
 * Both the mapped property names ('Ship Type') and the raw upstream ones
 * (`shipType`) are accepted, because this is called with either shape.
 */
export function buildVesselPopupBody(properties: any, actions = ''): string {
  const p = properties || {};
  // `||`, not `??`: a vessel that broadcasts no name arrives as '' and
  // `toVoyageVesselFeature` copies it through (it only skips null/undefined), so
  // `??` handed the card an empty heading instead of firing these fallbacks.
  // MMSI 0 is not a valid identity either, so it falls through the same way.
  const name = esc(p['Name'] || p['name'] || 'Unknown');
  const mmsi = esc(p['MMSI'] || p['mmsi'] || '—');
  const sog = p['Speed'] != null ? `${esc(p['Speed'])} kn` : '—';
  const cog = p['Course'] != null ? `${esc(Math.round(Number(p['Course'])))}°` : '—';
  // Only a literal hex colour is allowed through — it is interpolated into a
  // style attribute, where escaping alone would not make an arbitrary value safe.
  const color = /^#[0-9a-fA-F]{3,8}$/.test(String(p['Color'] ?? ''))
    ? String(p['Color'])
    : '#6B7280';
  const time = formatPopupTime(p['Timestamp UTC'] ?? p['time_utc']);
  const hdgRaw = p['Heading'] ?? p['heading'];
  const hdg = hdgRaw != null ? Math.round(Number(hdgRaw)) : null;

  return (
    `<div class="ais-popup__head"><span class="ais-popup__dot" style="background:${color}"></span>` +
      `<span class="ais-popup__name">${name}</span>${actions}</div>` +
    `<div class="ais-popup__row"><span class="ais-popup__k">MMSI:</span><span class="ais-popup__mmsi">${mmsi}</span></div>` +
    // Who, then WHERE, then the rest — a position belongs with the identity it
    // describes, not buried under the dimensions.
    positionRow(p) +
    row('Type', p['Ship Type'] ?? p['shipType']) +
    row('Status', p['Nav Status'] ?? p['navStatus']) +
    `<div class="ais-popup__row"><span class="ais-popup__k">SOG:</span><span class="ais-popup__v">${sog}</span></div>` +
    `<div class="ais-popup__row"><span class="ais-popup__k">COG:</span><span class="ais-popup__v">${cog}</span></div>` +
    (hdg != null
      ? `<div class="ais-popup__row"><span class="ais-popup__k">Heading:</span><span class="ais-popup__v">${esc(hdg)}°</span></div>`
      : '') +
    row('IMO', p['IMO'] ?? p['imo']) +
    row('Call Sign', p['Call Sign'] ?? p['callSign']) +
    row('Flag', p['Flag'] ?? p['flag']) +
    row('Country', p['Country'] ?? p['country']) +
    row('Length', p['Length'] ?? p['length'], ' m') +
    row('Beam', p['Beam'] ?? p['beam'], ' m') +
    // The lookup-only fields (GET /v1/vessel): present after a "Get latest",
    // absent on bbox/search data — `row()` renders nothing for those.
    row('Destination', p['Destination'] ?? p['destination'] ?? p['matchedDestination']) +
    row('From Port', p['From Port'] ?? p['fromPort']) +
    row('Max Draught', p['Max Draught'] ?? p['maxDraught'], ' m') +
    row('Rate of Turn', p['Rate of Turn'] ?? p['rate_of_turn']) +
    `<div class="ais-popup__row"><span class="ais-popup__k">Time:</span><span class="ais-popup__time">${time}</span></div>`
  );
}

// `VoyagePopupActions` was an alias for the promoted `VesselPopupActions`
// (phase 3). Phase 8b flipped every import to the real name and deleted it.


/**
 * The complete Voyage Radar vessel popup — an info card with a few icon actions
 * in its header.
 *
 * They live in the header rather than in a menu under the card. Full-width
 * buttons made a card that is already a dozen rows tall grow by another third,
 * and the card is a thing to READ — the actions are an aside. Track actions
 * live in the toolbar's trace panel for the same reason.
 *
 * At most FIVE of the six ever render together, because the first two are
 * mutually exclusive: the free save (only while the hull is NOT saved) or
 * take-off-the-map (only while it IS drawn as a located marker), then the paid
 * by-MMSI update, then port calls, then nearby vessels, then close. Each icon's
 * whole label is its tooltip — which is where a price has to live.
 *
 * Deleting the SAVED RECORD is deliberately not here. It is the one
 * permanent act in this set, and it belongs where its consequence is legible:
 * the Saved Vessels section, next to the row it would erase.
 *
 * Buttons are plain markup — Leaflet popups live outside Angular — so the map
 * controller click-delegates on `[data-mmsi]` + the class. The icons come from
 * the globally imported Material Icons font, the same one `mat-icon` uses.
 */
export function buildVesselPopup(
  properties: any,
  opts?: VoyagePopupActions,
): string {
  const p = properties || {};
  const mmsi = String(p['MMSI'] || p['mmsi'] || '').trim();
  const actions: string[] = [];
  if (mmsi && opts?.save === 'add') {
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__save" data-mmsi="${esc(mmsi)}" ` +
      `title="Watch vessel" aria-label="Watch vessel">` +
      `<span class="material-icons-outlined">bookmark_add</span></button>`,
    );
  }
  if (mmsi && opts?.lookupCredits != null) {
    const c = opts.lookupCredits;
    // One button, two jobs: the card starts without the lookup-only fields
    // (Destination, From Port, Max Draught, Rate of Turn), so the first press
    // fills them in and every later one re-checks them along with the fix.
    // "Update" is the one verb true of both.
    const label = `Update · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__latest" `
      + `data-card-action="latest" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — latest position and full details" aria-label="${label}">` +
      `<span class="material-icons-outlined">refresh</span></button>`,
    );
  }
  if (mmsi && opts?.portCallCredits != null) {
    const c = opts.portCallCredits;
    // One click opens the drawer AND fetches, every time — so the price is on
    // the button's own face rather than discovered after a panel slides open.
    const label = `Port calls · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__ports" `
      + `data-card-action="ports" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — arrivals, departures and time in port" aria-label="${label}">` +
      `<span class="material-icons-outlined">anchor</span></button>`,
    );
  }
  if (mmsi && opts?.nearbyCredits != null) {
    const c = opts.nearbyCredits;
    // Next to port calls because the two are the same kind of act — one click
    // that opens a drawer AND buys the answer in it — so the price is on the
    // button's own face here too, rather than discovered after a panel slides
    // open. What it does NOT choose is the radius: that is the drawer's own
    // "within __ km", remembered from the last search, so this button is
    // "search around this hull" and never "search 10 km around this hull".
    //
    // A PAID action, and the only thing that says so before the click is the
    // amber the paid family carries — which comes from the
    // `.ais-popup__latest, .ais-popup__ports` rule in `styles.scss`, not from
    // `.ais-popup__act`. This class has to join that selector or the button
    // renders bare beside two that do not.
    const label = `Nearby vessels · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__nearby" `
      + `data-card-action="nearby" data-mmsi="${esc(mmsi)}" ` +
      `title="${label} — who is around this hull right now" aria-label="${label}">` +
      `<span class="material-icons-outlined">radar</span></button>`,
    );
  }
  if (mmsi && opts?.removeFromMap) {
    // `layers_clear` because that is what this gesture means everywhere else in
    // Voyage Radar — both ON MAP bars and the Saved Vessels row use it for
    // exactly this, and it is the reversible one: the record stays, one click
    // redraws it.
    const label = 'Take off the map — stays in Watched Vessels';
    actions.push(
      `<button type="button" class="ais-popup__act ais-popup__offmap" data-mmsi="${esc(mmsi)}" ` +
      `title="${label}" aria-label="${label}">` +
      `<span class="material-icons-outlined">layers_clear</span></button>`,
    );
  }
  // Always last, and always present: the card is opened by a click on a hull,
  // and a click elsewhere on the map is the only other way to dismiss it —
  // which on a crowded chart means clicking another vessel by accident.
  actions.push(
    `<button type="button" class="ais-popup__act ais-popup__close" ` +
    `title="Close" aria-label="Close">` +
    `<span class="material-icons-outlined">close</span></button>`,
  );
  const menu = actions.length
    ? `<span class="ais-popup__acts">${actions.join('')}</span>`
    : '';
  // The hull is stamped on the card ROOT, not just on its buttons: four
  // different layers open this card (live, history, playback, located) and only
  // one of them goes through the map controller, so a `popupopen` listener
  // reading this attribute is the only way it can know whose card is on screen.
  const stamp = mmsi ? ` data-mmsi="${esc(mmsi)}"` : '';
  return `<div class="ais-popup"${stamp}>${buildVesselPopupBody(properties, menu)}</div>`;
}

/** How many hulls a cluster popup names before it summarises the rest. */
const CLUSTER_POPUP_LIMIT = 14;

/**
 * The card for a cluster that zoom cannot break apart — vessels moored on top of
 * one another, or a map already at its deepest zoom.
 *
 * Clicking a cluster normally frames its members, so this only appears where
 * that would do nothing. A count with no way to see what is inside it is a dead
 * end: the bubble says fourteen, and the operator's next question is which
 * fourteen. Names only, because a berth full of hulls is a list to read, not a
 * set of cards to page through — one click deeper on any of them is the vessel
 * popup itself.
 */
export function buildClusterPopup(
  vessels: { name?: any; mmsi?: any }[]
): string {
  const total = vessels.length;
  const shown = vessels.slice(0, CLUSTER_POPUP_LIMIT);
  const rest = total - shown.length;
  const rows = shown
    .map((v) => {
      // Same fallback chain as the vessel card: a hull that broadcasts no name
      // is still identified by its MMSI, and one with neither is still a hull.
      const name = esc(v.name || v.mmsi || 'Unknown');
      const mmsi = v.name && v.mmsi ? `<span class="ais-popup__k">${esc(v.mmsi)}</span>` : '';
      return `<div class="ais-popup__row"><span class="ais-popup__v">${name}</span>${mmsi}</div>`;
    })
    .join('');
  const more = rest > 0
    ? `<div class="ais-popup__row"><span class="ais-popup__k">+ ${rest} more</span></div>`
    : '';
  return (
    `<div class="ais-popup ais-popup--cluster">` +
      `<div class="ais-popup__head"><span class="ais-popup__name">${total} vessels here</span></div>` +
      rows +
      more +
    `</div>`
  );
}

// Voyage Radar's POI-nearby popup moved to `ais/voyage-radar/poi-nearby-popup.ts`
// (phase 8b). Its only consumers were that provider's POI feature and nearby
// controller, and a tasked POINT to be near is a Voyage Radar concept — the
// vessel card above is not, which is why only half of this file moved.
